import {
    ICashierService,
    OpenCashierOptions,
    OpenDepositOptions,
    OpenWithdrawOptions,
    OpenTransactionHistoryOptions
} from "../../../common/interfaces/cashier.service.interface";
import {ServiceBase} from "../ServiceBase";
import {CASHIER_SESSION_TYPES} from "../../../common/const/cashier-session-types";
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {ProductPackageToID} from "@sparkware/uc-sdk-core";
import {isCashoutSessionType, isDepositSessionType} from "../../../common/utils/cashier-util";
import {ICashierWidgetsEligibilityDetails} from "../../../common/interfaces/cashier-widgets-eligibility-details.interface";
import {IActionHandlerEventParams} from "../../../common/interfaces/casinofied-analytics-proxy.interface";
import {UserEligibilityController_TransactionHistoryWidgetEligibilityResponse} from "../api/api.service";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";

interface IEligibilityCheckResult {
    isEligible: boolean;
    businessCorrelationID: string;
    abCheckActionHandlerEventData?: IActionHandlerEventParams;
    eligibilityActionHandlerEventData?: IActionHandlerEventParams;
}

export class CashierService extends ServiceBase implements ICashierService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                this._transactionWidgetEligibilityPromise = null;
            });
    }

    getProductPackage(): ProductPackageToID {
        if (this.services.device.os.isMobileOS) {
            return ProductPackageToID.FULL_CASHIER_MOBILE;
        } else {
            return ProductPackageToID.FULL_CASHIER_PC;
        }
    }

    private async _waitForUserToBeLoggedIn(): Promise<void> {
        await this.services.user.waitForAutoLogin();

        if (!this.services.user.isAuthorized) {
            await this.services.user.openLogin();
        }
    }

    private _reportEligibilityEvents(eligibilityResult: IEligibilityCheckResult): void {
        if (eligibilityResult.abCheckActionHandlerEventData) {
            this.services.casinofiedAnalytics.actionHandler(eligibilityResult.abCheckActionHandlerEventData);
        }

        if (eligibilityResult.eligibilityActionHandlerEventData) {
            this.services.casinofiedAnalytics.actionHandler(eligibilityResult.eligibilityActionHandlerEventData);
        }
    }

    private async _getCashierWidgetEligibility(getEligibility: () => ICashierWidgetsEligibilityDetails,
        widgetConfigs: {force: boolean; enabled: boolean},
        analyticsOptions: {actionName: string}): Promise<IEligibilityCheckResult> {


        const eligibility = getEligibility();

        if (this.services.miniOrbit.isEnabled) {
            // In mini orbit, cashier is handled directly by the app.
            // For example, in poker, cashier is opened directly by the poker app
            return {
                isEligible: false,
                businessCorrelationID: ""
            };
        }

        const businessCorrelationID = this.services.tools.getGuid();

        if (widgetConfigs.force) {
            return {
                isEligible: true,
                businessCorrelationID: businessCorrelationID
            };
        }

        if (!widgetConfigs.enabled) {
            return {
                isEligible: false,
                businessCorrelationID: ""
            };
        }

        const abCheckActionHandlerEventData: IActionHandlerEventParams = {
            businessCorrelationID: businessCorrelationID,
            actionName: analyticsOptions.actionName,
            actionHandlerStep: 'ab_test_step',
            actionResult: {
                featureName: eligibility.featureName,
                testGroup: eligibility.testGroup,
                testID: eligibility.testID
            }
        };

        const eligibilityActionHandlerEventData: IActionHandlerEventParams = {
            businessCorrelationID: businessCorrelationID,
            actionName: analyticsOptions.actionName,
            actionHandlerStep: 'eligibility_check_step',
            actionResult: !eligibility.isABEligible
                ? null
                : {
                    isEligible: eligibility.isNtsEligible,
                    ineligibleReason: eligibility.ineligibleReason,
                    errorCode: eligibility.errorCode,
                    errorMessage: eligibility.errorMessage,
                    hasError: Boolean(eligibility.errorCode)
                }
        };

        return {
            isEligible: eligibility.isABEligible && eligibility.isNtsEligible,
            businessCorrelationID: businessCorrelationID,
            abCheckActionHandlerEventData: abCheckActionHandlerEventData,
            eligibilityActionHandlerEventData: eligibilityActionHandlerEventData
        };
    }

    async isEligibleForDepositWidget(): Promise<boolean> {
        const eligibility = await this._getDepositWidgetEligibility();
        return eligibility.isEligible;
    }

    async isCashierInRedirectMode(): Promise<boolean> {
        return this.services.device.os.isMobileOS && !(await this.isEligibleForDepositWidget());
    }

    private async _getDepositWidgetEligibility(): Promise<{isEligible: boolean, businessCorrelationID: string}> {

        if (!this.services.sdk.isDepositWidgetSupportedByHybrid()) {
            return {
                isEligible: false,
                businessCorrelationID: ""
            };
        }

        await this._waitForUserToBeLoggedIn();

        return this._getCashierWidgetEligibility(() => this.services.user.depositWidgetEligibility,
            this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_DEPOSIT,
            {
                actionName: 'deposit'
            });

    }

    private async _getCashOutWidgetEligibility(): Promise<IEligibilityCheckResult> {
        await this._waitForUserToBeLoggedIn();



        return this._getCashierWidgetEligibility(() => this.services.user.cashOutWidgetEligibility,
            this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_CASHOUT,
            {
                actionName: 'withdraw'
            });
    }

    private _transactionWidgetEligibilityPromise:  Promise<IEligibilityCheckResult> | null = null;
    private async _getTransactionHistoryWidgetEligibility(): Promise<IEligibilityCheckResult> {

        if(this._transactionWidgetEligibilityPromise) {
            return this._transactionWidgetEligibilityPromise;
        }

        const performEligibility = async () => {
            await this._waitForUserToBeLoggedIn();

            if(!this.services.user.isAuthorized) {
                throw new Error('Transaction history is not available for anonymous user');
            }

            const widgetConfigs = this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_PLAYER_TRANSACTIONS_HISTORY;
            const businessCorrelationID = this.services.tools.getGuid();

            if (widgetConfigs.force) {
                return {
                    isEligible: true,
                    businessCorrelationID: businessCorrelationID
                };
            }

            if (!widgetConfigs.enabled) {
                return {
                    isEligible: false,
                    businessCorrelationID: ""
                };
            }

            let ntsEligibility: UserEligibilityController_TransactionHistoryWidgetEligibilityResponse;

            try {
                ntsEligibility = await this.services.api.userEligibilityController.transactionHistoryWidgetEligibility({
                    LoginCountry: this.services.sdk.getCountryIso3()
                })
            } catch (err) {
                this.services.logger.error('Failed to call transactionHistoryWidgetEligibility', err);
                ntsEligibility = {
                    isEligible: false,
                    ineligibleReason: "error",
                    errorDescription: "Failed to query NTS transaction history eligibility",
                    errorID: 1,
                    hasErrors: true
                }
            }

            const abEligibility = this.services.abTestingFeatureDataProvider.transactionHistoryWidget

            const eligibilityData: ICashierWidgetsEligibilityDetails = {
                isABEligible: Boolean(abEligibility?.isEligible),
                isNtsEligible: ntsEligibility.isEligible,
                errorCode: ntsEligibility.errorID,
                errorMessage: ntsEligibility.errorDescription,
                ineligibleReason: ntsEligibility.ineligibleReason,
            };

            if(abEligibility) {
                eligibilityData.testID = abEligibility.testId;
                eligibilityData.featureName = abEligibility.featureName;
                eligibilityData.testGroup = abEligibility.testGroup;
            }

            return this._getCashierWidgetEligibility(() => eligibilityData,
                this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_PLAYER_TRANSACTIONS_HISTORY,
                {
                    actionName: 'transaction_history'
                });
        }

        this._transactionWidgetEligibilityPromise = performEligibility();
        return this._transactionWidgetEligibilityPromise;
    }

    async openDeposit(options: OpenDepositOptions): Promise<void> {

        const depositEligibility = await this._getDepositWidgetEligibility();

        this._reportEligibilityEvents(depositEligibility);

        if (depositEligibility.isEligible) {
            return this.services.appLauncher.openDepositWidget({
                businessCorrelationID: depositEligibility.businessCorrelationID || this.services.tools.getGuid(),
                sequentialCorrelationID: 1,
                channel: options.channel,
                appSpecificParameters: {
                    promoCode: options?.PromCode
                },
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CASHIER_SESSION_TYPES.QUICK_DEPOSIT,
                ...options
            });
        }
    }

    async openWithdraw(options: OpenWithdrawOptions): Promise<void> {
        const cashOutWidgetEligibility = await this._getCashOutWidgetEligibility();

        this._reportEligibilityEvents(cashOutWidgetEligibility);

        if (cashOutWidgetEligibility.isEligible) {
            await this.services.appLauncher.openCashoutWidget({
                businessCorrelationID: cashOutWidgetEligibility.businessCorrelationID || this.services.tools.getGuid(),
                channel: options.channel,
                sequentialCorrelationID: 1,
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CASHIER_SESSION_TYPES.FULL_CASHIER_CDH,
                ...options
            });
        }
    }

    async openTransactionHistory(options: OpenTransactionHistoryOptions): Promise<void> {
        const transactionHistoryWidgetEligibility = await this._getTransactionHistoryWidgetEligibility();
        this._reportEligibilityEvents(transactionHistoryWidgetEligibility);

        if (transactionHistoryWidgetEligibility.isEligible) {
            await this.services.appLauncher.openTransactionHistoryWidget({
                businessCorrelationID: transactionHistoryWidgetEligibility.businessCorrelationID || this.services.tools.getGuid(),
                channel: options.channel,
                sequentialCorrelationID: 1,
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CASHIER_SESSION_TYPES.TRANSACTIONS_HISTORY,
                ...options
            });
        }
    }

    async openCashier(options: OpenCashierOptions = {}, marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<void> {
        if (isDepositSessionType(options.sessionType)) {
            const depositEligibility = await this._getDepositWidgetEligibility();
            this._reportEligibilityEvents(depositEligibility);
            if (depositEligibility.isEligible) {
                return this.services.appLauncher.openDepositWidget({
                    businessCorrelationID: depositEligibility.businessCorrelationID || this.services.tools.getGuid(),
                    sequentialCorrelationID: 1,
                    channel: options.channel,
                    appSpecificParameters: {
                        promoCode: options?.PromCode || ""
                    }
                });
            }
        } else if (isCashoutSessionType(options.sessionType)) {
            const cashOutEligibility = await this._getCashOutWidgetEligibility();
            this._reportEligibilityEvents(cashOutEligibility);
            if (cashOutEligibility.isEligible) {
                return await this.services.appLauncher.openCashoutWidget({
                    businessCorrelationID: cashOutEligibility.businessCorrelationID || this.services.tools.getGuid(),
                    channel: options.channel,
                    sequentialCorrelationID: 1
                });
            }
        }

        await this._openCashierExternalLink(options, marketingParams, resolveByCallback);
    }

    async openOldCashier(options: OpenCashierOptions = {}): Promise<void> {
        await this._openCashierExternalLink(options);
    }

    private _openCashierExternalLink(options: OpenCashierOptions = {}, marketingParams?: MarketingParams, resolveByCallback: boolean = false) {
        this.services.sdk.openExternalLink({
            source: 'cashier',
            marketing: marketingParams,
            options: options
        }, resolveByCallback);
    }

    async openVerifyId(): Promise<void> {
        return this._openCashierExternalLink({
            sessionType: this.services.device.os.isMobileOS ? CASHIER_SESSION_TYPES.MOBILE_VERIFY_ID : CASHIER_SESSION_TYPES.VERIFY_ID
        });
    }

}